/**
 * Adding a Canonical Position Group, which is adding a **mapping**.
 *
 * A group is not a name somebody typed. A player's group is whatever
 * `canonicalise` reads out of a Source Profile's `position_map`, so a group no
 * label maps to has no members — and a statistic excluding it excludes nobody,
 * for ever, with nothing raising. So the dialog that adds one asks for the
 * group's name *and* the publisher's own labels that feed it, and this module
 * is the arithmetic behind that.
 *
 * **The dangerous part is that the map is replaced, not merged, by the server.**
 * `PATCH /source-profiles/{id}` sets the column to the body it is given. A
 * dialog that sent only its two new pairs would silently drop every mapping a
 * Source Template composed — Wyscout's profile declares twenty-seven of them —
 * and the next Run would cohort three-quarters of a division as `UNKNOWN`
 * while every screen looked right. `merged` is the whole answer to that, and is
 * why this is a module with tests rather than an object literal in a handler.
 *
 * Labels are upper-cased because that is how they are read: `canonicalise`
 * upper-cases a publisher's label before looking it up, and the server stores
 * them that way, so a map built in the case somebody typed would resolve
 * identically and *compare* differently against the labels a Run published.
 *
 * No imports, so `npm test` can execute it directly: the frontend ships no test
 * runner and bare `node --experimental-strip-types` cannot load React or Vite.
 */

/** A raw position label a Run published, as `/position-labels` serves it. */
export interface PositionLabel {
  label: string;
  canonical_position_group: string;
  players: number;
  /** Whether a map already answers for it. Served rather than inferred: a label
   *  deliberately mapped **to** `UNKNOWN` is mapped, and deriving it from the
   *  group would offer work already done. */
  mapped: boolean;
}

/** How a label is held in a position map, and compared against a Run's. */
export function normaliseLabel(label: string): string {
  return label.trim().toUpperCase();
}

/** A group name as it is stored: trimmed, and otherwise as typed — it is a name
 *  the dropdown shows and that `excluded_position_groups` holds by value, not a
 *  lookup key. */
export function normaliseGroup(group: string): string {
  return group.trim();
}

export type MappingProblem =
  | { ok: true; map: Record<string, string> }
  | { ok: false; field: "group" | "labels"; message: string };

/**
 * The profile's map with these labels pointed at this group.
 *
 * **Everything the profile already had is carried**, which is the point: the
 * server replaces the column with what it is sent, so anything left out is
 * deleted. A Source Template's composed mapping is not this dialog's to drop.
 */
export function merged(
  existing: Readonly<Record<string, string>>,
  labels: readonly string[],
  group: string,
): MappingProblem {
  const name = normaliseGroup(group);
  if (name === "") {
    return {
      ok: false,
      field: "group",
      message: "Give the group a name. It is what the dropdown will offer and what the ranking compares against.",
    };
  }

  const wanted = labels.map(normaliseLabel).filter(Boolean);
  if (wanted.length === 0) {
    return {
      ok: false,
      field: "labels",
      // The refusal this whole dialog exists to make. A group fed by no label
      // is the free-text group name that would sit in the dropdown excluding
      // nobody.
      message:
        "Choose at least one of the source's own labels, or add one it has not published yet. A group no label feeds has no players in it, so excluding it would do nothing.",
    };
  }

  const map: Record<string, string> = {};
  for (const [label, held] of Object.entries(existing)) {
    const key = normaliseLabel(label);
    if (key) map[key] = held;
  }
  for (const label of wanted) map[label] = name;
  return { ok: true, map };
}

export type TypedLabelProblem =
  | { ok: true; label: string }
  | { ok: false; message: string };

/**
 * A label the operator is adding by hand, checked against what is on offer.
 *
 * **Typing is the exception, and it stays the exception.** The list is offered
 * rather than typed because a publisher's vocabulary typed from memory into the
 * column that decides a player's cohort is how a group comes to exclude nobody
 * — a mapping for `ST` on a source that sends `St` is a mapping that never
 * fires, and nothing downstream can tell it from a typo.
 *
 * But a mapping for a label the latest Run did not publish is legitimate, which
 * `playersMoved` already says: a publisher may send it next season, a source
 * may not have run yet, and a cohort that only appears in a postseason file is
 * still a cohort somebody has to declare before the file arrives. So this
 * admits the label and refuses the three ways of adding one by accident.
 *
 * A label this source *does* publish is refused rather than accepted twice: the
 * row is already in the list with its count beside it, and the operator should
 * tick the measured one rather than create a second entry that agrees with it
 * only until one of them is edited.
 */
export function typedLabel(
  raw: string,
  published: readonly PositionLabel[],
  typed: readonly string[],
): TypedLabelProblem {
  const label = normaliseLabel(raw);
  if (label === "") {
    return {
      ok: false,
      message: "Type the label the publisher sends, spelled the way it sends it.",
    };
  }
  const already = published.find((row) => normaliseLabel(row.label) === label);
  if (already) {
    return {
      ok: false,
      message: `This source already publishes ${already.label}. Tick it in the list instead, so the group is made from the measured label rather than a second copy of it.`,
    };
  }
  if (typed.some((held) => normaliseLabel(held) === label)) {
    return { ok: false, message: `${label} has already been added.` };
  }
  return { ok: true, label };
}

/**
 * Labels this would move out of a group they are already in.
 *
 * Not a refusal — re-pointing a label is a legitimate correction, and the
 * reason `position_map` can override the sport default at all. But it changes
 * which cohort those players are ranked in on the next Run, so the dialog says
 * whose it is taking before the button is pressed rather than after.
 */
export function reassigned(
  existing: Readonly<Record<string, string>>,
  labels: readonly string[],
  group: string,
): { label: string; from: string }[] {
  const name = normaliseGroup(group);
  const held: Record<string, string> = {};
  for (const [label, value] of Object.entries(existing)) held[normaliseLabel(label)] = value;

  const moves: { label: string; from: string }[] = [];
  for (const raw of labels) {
    const label = normaliseLabel(raw);
    const from = label ? held[label] : undefined;
    if (from !== undefined && from !== name) moves.push({ label, from });
  }
  return moves;
}

/**
 * How many published players this mapping would move, from the Run's own count.
 *
 * The figure that turns "add a group" into a decision. A label the latest Run
 * never published counts nought — the mapping is still legal, because a
 * publisher may send it next season, but the dialog should not imply it moves
 * anybody today.
 */
export function playersMoved(
  published: readonly PositionLabel[],
  labels: readonly string[],
): number {
  const wanted = new Set(labels.map(normaliseLabel));
  return published
    .filter((row) => wanted.has(normaliseLabel(row.label)))
    .reduce((total, row) => total + row.players, 0);
}

/**
 * Discovery's reading of a payload, as the dialog's label list.
 *
 * **Add Source's substitute for a Run.** A published Run records which group
 * each label landed in; an unsaved source has never had one, so the group is
 * worked out here from the map the profile *will* be created with — and a label
 * nothing maps reads `UNKNOWN`, which is exactly what a Run would have recorded
 * for it.
 *
 * The result is the same shape a Run produces, so nothing downstream of this
 * can tell the two apart. That is what keeps Add Source and the saved screens
 * sharing one dialog rather than growing two that drift.
 */
/**
 * One vocabulary from the several a multi-Feed source publishes it across.
 *
 * **A partitioning publisher splits its players between Feeds, so no single
 * Feed holds the vocabulary.** ESPN's college football source is four Feeds —
 * Offense, Defense, Scoring, Special Teams — and each sends only its own slice:
 * the position field samples `QB` on the first, `LB` on the second, `RB` on the
 * third, `PK` on the fourth. Discovery reports one row per Feed per path, so
 * reading the first match offered `QB` and nothing else, which is not a
 * publisher's vocabulary but a quarter of one, and nothing on the screen said
 * so.
 *
 * Summed per label and ordered commonest first, ties by label — the same order
 * the server already applies within one Feed, so a source with one Feed is
 * unchanged by passing through here.
 */
export function unionedLabels(
  perFeed: readonly { values: readonly { value: string; records: number }[] }[],
): { value: string; records: number }[] {
  const totals = new Map<string, number>();
  for (const field of perFeed) {
    for (const row of field.values) {
      totals.set(row.value, (totals.get(row.value) ?? 0) + row.records);
    }
  }
  return [...totals]
    .map(([value, records]) => ({ value, records }))
    .sort((a, b) => b.records - a.records || (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
}

export function labelsFromPayload(
  values: readonly { value: string; records: number }[],
  positionMap: Readonly<Record<string, string>>,
): PositionLabel[] {
  const held: Record<string, string> = {};
  for (const [label, group] of Object.entries(positionMap)) {
    held[normaliseLabel(label)] = group;
  }
  return values.map((row) => {
    const group = held[normaliseLabel(row.value)];
    return {
      label: row.value,
      canonical_position_group: group ?? "UNKNOWN",
      players: row.records,
      mapped: group !== undefined,
    };
  });
}

/**
 * The labels worth deciding about first: unmapped, then by how many players
 * carry them.
 *
 * An unmapped label is a position falling to `UNKNOWN` — the thing somebody
 * opened this dialog about — and the server already orders by count, so this
 * only lifts that block to the top without disturbing it.
 */
export function needingAttentionFirst(published: readonly PositionLabel[]): PositionLabel[] {
  const unmapped = published.filter((row) => !row.mapped);
  const mapped = published.filter((row) => row.mapped);
  return [...unmapped, ...mapped];
}
